import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import TimeSheetFilter from "./TimeSheetFilter";
import Typography from "../../Utility/Typography";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import CommonModal from "../../Utility/CommonModel/CommonModal";
import Button from "../../Utility/Button/Button";
import ToggleSwitch from "../../Utility/ToggleSwitch/ToggleSwitch";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { buildUserDropdownOptionsFromUsersData } from "../../utils/userFilterOptions";
import LogTimeModal from "../Issues/LogTimeModal/LogTimeModal";
import { getTodayDateRange } from "../../utils/dateUtils";
import { fetchTeamTimeSummary, fetchDeveloperTimeSummary } from "../../Redux/Thunks/timeEntryThunk";
import { getListFromApiResponse } from "../utils/apiListMappers";
import { mapProjectsToFilterOptions } from "../Projects/projectMappers";
import {
  buildTimeSummaryIdQueryParams,
  buildTeamSummaryExportQueryParams,
  buildDeveloperSummaryExportQueryParams,
} from "../../utils/timeEntryQueryParams";
import {
  downloadTeamSummaryExport,
  downloadDeveloperSummaryExport,
} from "../../utils/downloadTimeLogsExport";
import { showSuccess, showError } from "../../utils/toast";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { APP_ROLE } from "../../permissions/roleKinds";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import {
  formatDurationWithUnit,
  parseDurationToMinutes,
  resolveGrandTotalFromMetaOrRows,
} from "./timeEntryDisplayUtils";
import TimeEntryTableTotalsBar from "./TimeEntryTableTotalsBar";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import {
  LIST_PAGE_SIZE_OPTIONS,
  readStoredListPageSize,
  writeStoredListPageSize,
  resolveListTotalPages,
  TIME_ENTRY_TEAM_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY,
  TIME_ENTRY_DEVELOPER_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY,
} from "../../Utility/Pagination/listPaginationConstants";

function TeamTimeSummary({
  data,
  loading = false,
  error = null,
  developerData,
  developerLoading = false,
  developerError = null,
  dateRangeLabel,
  onPrevWeek,
  onNextWeek,
  disableNext,
  weekDateFrom,
  weekDateTo,
  filterExpanded,
  onFilterExpandedChange,
  /** Parent sets day nav to today so header matches filter after Reset */
  onResetToToday,
}) {
  const dispatch = useDispatch();
  const { can, roleKind } = usePermissions();
  const showTeamFilter = roleKind === APP_ROLE.ADMIN;
  const teamsData = useSelector(selectTeamsData);
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const usersData = useSelector(selectUsersData);
  const teamsListLen = getListFromApiResponse(teamsData).length;
  const clientsListLen = getListFromApiResponse(clientsData).length;
  const projectsListLen = getListFromApiResponse(projectsData).length;
  const usersListLen = getListFromApiResponse(usersData).length;

  const [showDeveloperTimeEntry, setShowDeveloperTimeEntry] = useState(false);

  // Fetch teams data on mount
  useEffect(() => {
    if (showTeamFilter && teamsListLen === 0) dispatch(fetchTeamsData({ getAll: true }));
    if (clientsListLen === 0) dispatch(fetchClientsData({ getAll: true }));
    if (projectsListLen === 0) dispatch(fetchProjectsData({ getAll: true }));
  }, [dispatch, showTeamFilter, teamsListLen, clientsListLen, projectsListLen]);

  // Fetch users data when the Developer Time Entry filter (User dropdown) is shown
  useEffect(() => {
    if (showDeveloperTimeEntry && usersListLen === 0) dispatch(fetchUsersData({ getAll: true }));
  }, [dispatch, showDeveloperTimeEntry, usersListLen]);

  /* ===================== FILTER STATE (draft/apply) ===================== */
  const [draftFilters, setDraftFilters] = useState({
    client: [], // multiselect values
    project: [], // multiselect values
    team: [], // multiselect values
    user: [], // multiselect values (Developer filter, shown when showDeveloperTimeEntry is on)
  });
  const [appliedFilters, setAppliedFilters] = useState({
    client: [],
    project: [],
    team: [],
    user: [],
  });

  const [draftDateRange, setDraftDateRange] = useState(null); // { startDate, endDate }
  const [appliedDateRange, setAppliedDateRange] = useState(null);

  const [isLogTimeOpen, setIsLogTimeOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const [selectedDeveloperLog, setSelectedDeveloperLog] = useState(null);
  const [isDeveloperLogModalOpen, setIsDeveloperLogModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    readStoredListPageSize(TIME_ENTRY_TEAM_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY)
  );
  const [devPage, setDevPage] = useState(1);
  const [devPageSize, setDevPageSize] = useState(() =>
    readStoredListPageSize(TIME_ENTRY_DEVELOPER_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY)
  );

  /** Parent-selected day (YYYY-MM-DD); default API + filter range when user has not applied another range */
  const defaultDateRangeForFilter = useMemo(() => {
    if (!weekDateFrom || !weekDateTo) return null;
    return { startDate: weekDateFrom, endDate: weekDateTo };
  }, [weekDateFrom, weekDateTo]);

  const rangeForApi = appliedDateRange ?? defaultDateRangeForFilter;

  useEffect(() => {
    setPage(1);
    setDevPage(1);
  }, [weekDateFrom, weekDateTo]);

  const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}:${mins.toString().padStart(2, "0")} hr`;
  };

  /* ===================== NORMALIZE & GROUP DATA (DATE → TASK) ===================== */
  const groupedData = useMemo(() => {
    if (!data) return [];

    let groups = [];

    if (Array.isArray(data.groups)) {
      groups = data.groups;
    } else if (Array.isArray(data.list)) {
      // API sends flat list: group by full-date field in "day"
      const byDate = new Map();
      data.list.forEach((entry) => {
        const dateLabel = entry.day || entry.dateLabel || "Timesheet";
        if (!byDate.has(dateLabel)) {
          byDate.set(dateLabel, { dateLabel, entries: [] });
        }
        byDate.get(dateLabel).entries.push(entry);
      });
      groups = Array.from(byDate.values());
    }

    if (!Array.isArray(groups) || groups.length === 0) return [];

    return groups.map((group) => {
      // First aggregate by (member + task) for this date — server already applied filters
      const aggregateMap = new Map();

      (group.entries || []).forEach((entry) => {
        const memberName = entry.member || entry.assignee || entry.owner || "";
        const teamName = entry.team || "";

        // memberName is already declared above in the same loop iteration
        const taskName = entry.task || "No Task";
        // Include team in key to properly aggregate by team, member, and task
        const key = `${teamName}__${memberName}__${taskName}`;

        const durationStr = entry.duration || entry.time || "";
        const minutes = parseDurationToMinutes(durationStr);

        if (!aggregateMap.has(key)) {
          aggregateMap.set(key, {
            id: entry.id,
            team: teamName,
            member: memberName,
            task: taskName,
            date: group.dateLabel,
            totalMinutes: 0,
            logs: [],
          });
        }

        const agg = aggregateMap.get(key);
        agg.totalMinutes += minutes;
        if (entry.description) {
          agg.logs.push(entry.description);
        }
      });

      // Now regroup by task so UI can show "group by task" under each date
      const taskMap = new Map();
      aggregateMap.forEach((agg) => {
        const taskName = agg.task;
        const row = {
          id: agg.id,
          team: agg.team,
          member: agg.member,
          task: agg.task,
          date: agg.date,
          time: formatMinutes(agg.totalMinutes),
          logs: agg.logs,
        };

        if (!taskMap.has(taskName)) {
          taskMap.set(taskName, []);
        }
        taskMap.get(taskName).push(row);
      });

      return {
        dateLabel: group.dateLabel,
        tasks: Array.from(taskMap.entries()).map(([task, entries]) => ({
          task,
          entries,
        })),
      };
    });
  }, [data]);

  const isTeamSummaryApiShape = useMemo(() => {
    if (!Array.isArray(data?.list)) return false;
    return data.list.some((u) => u && u.logCount != null);
  }, [data]);

  const summaryRows = useMemo(() => {
    if (!isTeamSummaryApiShape) return [];
    return (data?.list || []).map((u, idx) => ({
      id: u.id || idx,
      teamId: u.teamId,
      teamName: u.teamName || "----",
      clientName: u.clientName,
      projectName: u.projectName,
      epicId: u.epicId,
      epicName: u.epicName,
      epicKey: u.epicKey,
      time: u.totalFormatted || u.time || formatMinutes(Number(u.totalMinutes) || 0),
      billableMinutes: u.billableMinutes,
      billableTime:
        u.billableFormatted || u.billableTime || formatMinutes(Number(u.billableMinutes) || 0),
      lastLogDate: u.lastLogDate,
      logCount: Number(u.logCount) || 0,
      details: Array.isArray(u.details) ? u.details : [],
    }));
  }, [data, isTeamSummaryApiShape]);

  // Flatten grouped data into simple row list for UI rendering (legacy shape)
  const rows = useMemo(() => {
    if (isTeamSummaryApiShape) return summaryRows;
    return groupedData.flatMap((group) => group.tasks.flatMap((taskGroup) => taskGroup.entries));
  }, [groupedData, isTeamSummaryApiShape, summaryRows]);

  /** Normalized keys for {@link useSorting} — API vs legacy columns differ; sort fields align. */
  const rowsWithSortKeys = useMemo(() => {
    if (!rows.length) return [];
    if (isTeamSummaryApiShape) {
      return rows.map((entry) => ({
        ...entry,
        sortTeam: entry.teamName ?? "",
        sortClient: entry.clientName ?? "",
        sortProject: entry.projectName ?? "",
        sortEpic: entry.epicName ?? entry.epicKey ?? "",
        sortDate: entry.lastLogDate ?? "",
        sortTime: entry.billableTime ?? "",
      }));
    }
    return rows.map((entry) => ({
      ...entry,
      sortTeam: entry.team ?? "",
      sortMember: entry.member ?? "",
      sortTaskOrWork: entry.task ?? "",
      sortDate: entry.date ?? "",
      sortTime: entry.time ?? "",
    }));
  }, [rows, isTeamSummaryApiShape]);

  const {
    sortedData: sortedRows,
    sortKey,
    sortDirection,
    handleSort,
  } = useSorting(rowsWithSortKeys, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  /* ===================== FILTER OPTIONS ===================== */
  const filterOptions = useMemo(() => {
    const clientsList = getListFromApiResponse(clientsData);
    const teamsList = getListFromApiResponse(teamsData);

    const clients = clientsList
      .map((c) => c.clientName ?? c.name ?? "")
      .filter(Boolean)
      .sort();

    const teams = teamsList
      .map((t) => t.teamName ?? t.name ?? "")
      .filter(Boolean)
      .sort();

    return {
      clients,
      projects: mapProjectsToFilterOptions(projectsData),
      teams,
      users: buildUserDropdownOptionsFromUsersData(usersData),
    };
  }, [clientsData, projectsData, teamsData, usersData]);

  /**
   * Developer time entry rows — GET /time-logs/developer-summary, one row per (developer, work
   * item, date). `totalMinutesForDate` is already summed server-side across all of that
   * developer's work items for the date, so every row sharing (developerId, logDate) carries the
   * same total — the frontend just merges those rows with rowSpan (see below).
   */
  const developerRows = useMemo(() => {
    if (!Array.isArray(developerData?.list)) return [];
    return developerData.list.map((row, idx) => ({
      id: row.id || `dev-row-${idx}`,
      developerId: row.developerId,
      developerName: row.developerName,
      teamName: row.teamName,
      clientName: row.clientName,
      projectName: row.projectName,
      epicName: row.epicName || row.epicKey,
      workItemName: row.workItemTitle || row.workItemKey,
      date: row.logDate,
      loggedMinutes: row.loggedMinutes,
      loggedTime: row.loggedFormatted || formatMinutes(Number(row.loggedMinutes) || 0),
      totalMinutesForDate: row.totalMinutesForDate,
      totalTime: row.totalFormatted || formatMinutes(Number(row.totalMinutesForDate) || 0),
      details: Array.isArray(row.details) ? row.details : [],
      sortDeveloper: row.developerName ?? "",
      sortTeam: row.teamName ?? "",
      sortClient: row.clientName ?? "",
      sortProject: row.projectName ?? "",
      sortEpic: row.epicName ?? row.epicKey ?? "",
      sortWorkItem: row.workItemTitle ?? row.workItemKey ?? "",
      sortDate: row.logDate ?? "",
    }));
  }, [developerData]);

  const {
    sortedData: sortedDeveloperRows,
    sortKey: developerSortKey,
    sortDirection: developerSortDirection,
    handleSort: handleDeveloperSort,
  } = useSorting(developerRows, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  /** Merges the Total Hours cell (rowSpan) across consecutive rows sharing the same developer + date. */
  const developerRowsWithTotalRowSpan = useMemo(() => {
    return sortedDeveloperRows.map((entry, idx) => {
      const groupKey = `${entry.developerId ?? entry.developerName}__${entry.date}`;
      const prevEntry = sortedDeveloperRows[idx - 1];
      const isGroupStart =
        !prevEntry ||
        `${prevEntry.developerId ?? prevEntry.developerName}__${prevEntry.date}` !== groupKey;

      let totalRowSpan = 1;
      if (isGroupStart) {
        for (let i = idx + 1; i < sortedDeveloperRows.length; i += 1) {
          const nextEntry = sortedDeveloperRows[i];
          if (`${nextEntry.developerId ?? nextEntry.developerName}__${nextEntry.date}` !== groupKey)
            break;
          totalRowSpan += 1;
        }
      }

      return { ...entry, isTotalGroupStart: isGroupStart, totalRowSpan };
    });
  }, [sortedDeveloperRows]);

  const developerGrandTotal = useMemo(
    () =>
      resolveGrandTotalFromMetaOrRows(developerData?.meta, developerRows, (row) =>
        row?.loggedMinutes != null && Number.isFinite(Number(row.loggedMinutes))
          ? Number(row.loggedMinutes)
          : parseDurationToMinutes(row?.loggedTime)
      ),
    [developerData?.meta, developerRows]
  );

  const devTotalPages = useMemo(
    () => resolveListTotalPages(developerData?.meta, devPageSize, developerRows.length),
    [developerData?.meta, devPageSize, developerRows.length]
  );

  const grandTotal = useMemo(
    () =>
      resolveGrandTotalFromMetaOrRows(data?.meta, rows, (row) =>
        row?.totalMinutes != null && Number.isFinite(Number(row.totalMinutes))
          ? Number(row.totalMinutes)
          : parseDurationToMinutes(row?.time)
      ),
    [data?.meta, rows]
  );

  /** Adapts team-summary meta's billable fields onto the shape {@link resolveGrandTotalFromMetaOrRows} expects. */
  const billableMetaForTotals = useMemo(() => {
    const meta = data?.meta;
    if (!meta) return null;
    return {
      totalHours: meta.totalBillableHours,
      grandTotalFormatted: meta.grandTotalBillableFormatted,
      grandTotalMinutes: meta.grandTotalBillableMinutes,
    };
  }, [data?.meta]);

  const grandBillableTotal = useMemo(
    () =>
      resolveGrandTotalFromMetaOrRows(billableMetaForTotals, rows, (row) =>
        row?.billableMinutes != null && Number.isFinite(Number(row.billableMinutes))
          ? Number(row.billableMinutes)
          : parseDurationToMinutes(row?.billableTime)
      ),
    [billableMetaForTotals, rows]
  );

  const totalPages = useMemo(
    () => resolveListTotalPages(data?.meta, pageSize, rows.length),
    [data?.meta, pageSize, rows.length]
  );

  /* ===================== HANDLERS ===================== */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const cleared = { client: [], project: [], team: [], user: [] };
    const todayRange = getTodayDateRange();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setDraftDateRange(todayRange);
    setAppliedDateRange(todayRange);
    setPage(1);
    onResetToToday?.();
  };

  const applyFiltersHandler = () => {
    setAppliedFilters(draftFilters);
    setAppliedDateRange(draftDateRange ?? defaultDateRangeForFilter);
    setPage(1);
    onFilterExpandedChange?.(true);
  };

  const toYMD = (raw) => {
    if (!raw) return undefined;
    const s = String(raw);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 10);
  };

  const teamSummaryQueryParams = useMemo(() => {
    const dateFrom = toYMD(rangeForApi?.startDate) || undefined;
    const dateTo = toYMD(rangeForApi?.endDate) || undefined;
    const filtersForApi = showTeamFilter ? appliedFilters : { ...appliedFilters, team: [] };
    return {
      ...buildTimeSummaryIdQueryParams({
        appliedFilters: filtersForApi,
        clientsData,
        teamsData,
        projectsData,
      }),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page,
      limit: pageSize,
    };
  }, [
    appliedFilters,
    showTeamFilter,
    clientsData,
    teamsData,
    projectsData,
    rangeForApi?.startDate,
    rangeForApi?.endDate,
    page,
    pageSize,
  ]);

  // Server-side: GET /time-logs/team-summary — dateFrom/dateTo + optional clientId, teamId, projectId, userId
  useEffect(() => {
    dispatch(fetchTeamTimeSummary(teamSummaryQueryParams));
  }, [dispatch, teamSummaryQueryParams]);

  const developerSummaryQueryParams = useMemo(() => {
    const dateFrom = toYMD(rangeForApi?.startDate) || undefined;
    const dateTo = toYMD(rangeForApi?.endDate) || undefined;
    const filtersForApi = showTeamFilter ? appliedFilters : { ...appliedFilters, team: [] };
    return {
      ...buildTimeSummaryIdQueryParams({
        appliedFilters: filtersForApi,
        clientsData,
        teamsData,
        projectsData,
      }),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page: devPage,
      limit: devPageSize,
    };
  }, [
    appliedFilters,
    showTeamFilter,
    clientsData,
    teamsData,
    projectsData,
    rangeForApi?.startDate,
    rangeForApi?.endDate,
    devPage,
    devPageSize,
  ]);

  // Server-side: GET /time-logs/developer-summary — only fetched while the developer table is shown
  useEffect(() => {
    if (showDeveloperTimeEntry) dispatch(fetchDeveloperTimeSummary(developerSummaryQueryParams));
  }, [dispatch, showDeveloperTimeEntry, developerSummaryQueryParams]);

  const refetchTeamSummaryAfterLog = useCallback(() => {
    dispatch(fetchTeamTimeSummary(teamSummaryQueryParams));
    if (showDeveloperTimeEntry) dispatch(fetchDeveloperTimeSummary(developerSummaryQueryParams));
  }, [dispatch, teamSummaryQueryParams, showDeveloperTimeEntry, developerSummaryQueryParams]);

  const tryOpenLogTime = () => {
    if (!can(PERMISSION_KEYS.TIME_ENTRY_CREATE)) {
      setDeniedPermissionKey(PERMISSION_KEYS.TIME_ENTRY_CREATE);
      return;
    }
    setIsLogTimeOpen(true);
  };

  const handleExportTeamSummaryCsv = useCallback(async () => {
    try {
      const params = buildTeamSummaryExportQueryParams(teamSummaryQueryParams);
      await downloadTeamSummaryExport(params, "csv");
      showSuccess("Team summary export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [teamSummaryQueryParams]);

  const handleExportTeamSummaryXlsx = useCallback(async () => {
    try {
      const params = buildTeamSummaryExportQueryParams(teamSummaryQueryParams);
      await downloadTeamSummaryExport(params, "xlsx");
      showSuccess("Team summary export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [teamSummaryQueryParams]);

  const handleExportDeveloperSummaryCsv = useCallback(async () => {
    try {
      const params = buildDeveloperSummaryExportQueryParams(developerSummaryQueryParams);
      await downloadDeveloperSummaryExport(params, "csv");
      showSuccess("Developer summary export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [developerSummaryQueryParams]);

  const handleExportDeveloperSummaryXlsx = useCallback(async () => {
    try {
      const params = buildDeveloperSummaryExportQueryParams(developerSummaryQueryParams);
      await downloadDeveloperSummaryExport(params, "xlsx");
      showSuccess("Developer summary export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [developerSummaryQueryParams]);

  /* ===================== UI ===================== */
  return (
    <div className="w-full">
      <TimeSheetFilter
        filters={draftFilters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        showTask={false} // task dropdown not showing
        showTeam={showTeamFilter}
        showUser={showDeveloperTimeEntry} // User filter — only visible in developer time entry view
        showStatus={false} // hide status dropdown
        showBillingType={false} // hide billing type dropdown
        onLogTime={tryOpenLogTime}
        onSync={() =>
          showDeveloperTimeEntry
            ? dispatch(fetchDeveloperTimeSummary(developerSummaryQueryParams))
            : dispatch(fetchTeamTimeSummary(teamSummaryQueryParams))
        }
        onExportCsv={
          showDeveloperTimeEntry ? handleExportDeveloperSummaryCsv : handleExportTeamSummaryCsv
        }
        onExportXlsx={
          showDeveloperTimeEntry ? handleExportDeveloperSummaryXlsx : handleExportTeamSummaryXlsx
        }
        dateRangeLabel={dateRangeLabel}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        disableNext={disableNext}
        dateRangeDraft={draftDateRange ?? defaultDateRangeForFilter}
        onDateRangeDraftChange={setDraftDateRange}
        handleFilterReset={handleFilterReset}
        applyFilters={applyFiltersHandler}
        filterExpanded={filterExpanded}
        onFilterExpandedChange={onFilterExpandedChange}
      />

      {isLogTimeOpen && (
        <LogTimeModal
          isOpen={isLogTimeOpen}
          onClose={() => setIsLogTimeOpen(false)}
          issue={null}
          onSuccess={refetchTeamSummaryAfterLog}
        />
      )}

      <div className="flex items-center gap-2 mt-3">
        <Typography variant="text2" className="text-text-primary whitespace-nowrap m-0 p-0">
          Show Developer Time Entry
        </Typography>
        <ToggleSwitch
          checked={showDeveloperTimeEntry}
          onChange={setShowDeveloperTimeEntry}
          size="sm"
          aria-label="Show Developer Time Entry"
        />
      </div>

      {showDeveloperTimeEntry ? (
        <ContentLoader loading={developerLoading} className="min-h-[40vh]">
          {!developerLoading && developerError ? (
            <NoDataFound message={developerError || "Failed to load developer time summary"} />
          ) : !developerLoading && !developerRows.length ? (
            <NoDataFound message="No developer time summary data available" />
          ) : !developerLoading ? (
            <div className="mt-4">
              <TimeEntryTableTotalsBar
                dateRangeLabel={dateRangeLabel}
                totalLabel={developerGrandTotal.totalLabel}
              />
              <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full mb-0 clients-table">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <SortableHeader
                              columnKey="sortDate"
                              label="Date"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="sortDeveloper"
                              label="Developer Name"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="sortTeam"
                              label="Team"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="sortClient"
                              label="Client"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="sortProject"
                              label="Project"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="sortEpic"
                              label="Epic"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="sortWorkItem"
                              label="Work Item"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="loggedTime"
                              label="Logged Hours"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable={false}
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="totalTime"
                              label="Total Hours"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable={false}
                              className="px-4 py-2 align-middle"
                            />
                            <SortableHeader
                              columnKey="logs"
                              label="Logs"
                              onSort={handleDeveloperSort}
                              currentSortKey={developerSortKey}
                              sortDirection={developerSortDirection}
                              sortable={false}
                              className="px-4 py-2 align-middle action-col"
                            />
                          </tr>
                        </thead>
                        <tbody className="relative">
                          {developerRowsWithTotalRowSpan.map((entry) => (
                            <tr key={entry.id} className="border-t border-border-primary">
                              {entry.isTotalGroupStart ? (
                                <td
                                  rowSpan={entry.totalRowSpan}
                                  className="text-start align-middle px-4 py-2 border-r border-border-primary"
                                >
                                  <Typography>{entry.date || "-"}</Typography>
                                </td>
                              ) : null}
                              <td className="text-start align-middle px-4 py-2">
                                <Typography className="font-medium">
                                  {entry.developerName || "-"}
                                </Typography>
                              </td>
                              <td className="text-start align-middle px-4 py-2">
                                <Typography>{entry.teamName || "-"}</Typography>
                              </td>
                              <td className="text-start align-middle px-4 py-2">
                                <Typography>{entry.clientName || "-"}</Typography>
                              </td>
                              <td className="text-start align-middle px-4 py-2">
                                <Typography>{entry.projectName || "-"}</Typography>
                              </td>
                              <td className="text-start align-middle px-4 py-2">
                                <Typography>{entry.epicName || "-"}</Typography>
                              </td>
                              <td className="text-start align-middle px-4 py-2">
                                <Typography>{entry.workItemName || "-"}</Typography>
                              </td>
                              <td className="text-start align-middle px-4 py-2">
                                <Typography>{entry.loggedTime}</Typography>
                              </td>
                              {entry.isTotalGroupStart ? (
                                <td
                                  rowSpan={entry.totalRowSpan}
                                  className="text-start align-middle px-4 py-2 border-l border-border-primary bg-surface-secondary/40"
                                >
                                  <Typography className="font-medium">{entry.totalTime}</Typography>
                                </td>
                              ) : null}
                              <td className="text-start align-middle action-col px-4 py-2 !whitespace-nowrap min-w-[4.75rem]">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  className="whitespace-nowrap shrink-0"
                                  onClick={() => {
                                    setSelectedDeveloperLog(entry);
                                    setIsDeveloperLogModalOpen(true);
                                  }}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              {developerRows.length > 0 ? (
                <ListPaginationBar
                  currentPage={devPage}
                  totalPages={devTotalPages}
                  pageSize={devPageSize}
                  onPageChange={setDevPage}
                  onPageSizeChange={(next) => {
                    writeStoredListPageSize(
                      TIME_ENTRY_DEVELOPER_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY,
                      next
                    );
                    setDevPageSize(next);
                    setDevPage(1);
                  }}
                  pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                  className="px-2 pb-2"
                />
              ) : null}
            </div>
          ) : null}
        </ContentLoader>
      ) : (
        <ContentLoader loading={loading} className="min-h-[40vh]">
          {!loading && error ? (
            <NoDataFound message={error || "Failed to load team time summary"} />
          ) : !loading && !rows.length ? (
            <NoDataFound message="No team time summary data available" />
          ) : !loading ? (
            <div className="mt-4">
              <TimeEntryTableTotalsBar
                dateRangeLabel={dateRangeLabel}
                loggedLabel={isTeamSummaryApiShape ? grandTotal.totalLabel : undefined}
                billableLabel={isTeamSummaryApiShape ? grandBillableTotal.totalLabel : undefined}
                totalLabel={isTeamSummaryApiShape ? undefined : grandTotal.totalLabel}
              />
              <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full mb-0 clients-table">
                        <thead className="bg-surface-secondary">
                          {isTeamSummaryApiShape ? (
                            <tr>
                              <SortableHeader
                                columnKey="sortTeam"
                                label="Team"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="sortClient"
                                label="Client"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="sortProject"
                                label="Project"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="sortEpic"
                                label="Epic"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="loggedHours"
                                label="Logged Hours"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="sortTime"
                                label="Billable Hours"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="log"
                                label="Log"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="px-4 py-2 align-middle action-col"
                              />
                            </tr>
                          ) : (
                            <tr>
                              <SortableHeader
                                columnKey="sortTaskOrWork"
                                label="Task"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="loggedHours"
                                label="Logged Hours"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="sortDate"
                                label="Date"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="sortTime"
                                label="Time"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable
                                className="px-4 py-2 align-middle"
                              />
                              <SortableHeader
                                columnKey="log"
                                label="Log"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="px-4 py-2 align-middle action-col"
                              />
                            </tr>
                          )}
                        </thead>
                        <tbody className="relative">
                          {sortedRows.map((entry) => (
                            <tr key={entry.id} className="border-t border-border-primary">
                              {isTeamSummaryApiShape ? (
                                <>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography className="font-medium">
                                      {entry.teamName || "-"}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{entry.clientName || "-"}</Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{entry.projectName || "-"}</Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>
                                      {entry.epicName || entry.epicKey || "-"}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{formatDurationWithUnit(entry.time)}</Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>
                                      {formatDurationWithUnit(entry.billableTime)}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle action-col px-4 py-2 !whitespace-nowrap min-w-[4.75rem]">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      className="whitespace-nowrap shrink-0"
                                      onClick={() => {
                                        setSelectedLog(entry);
                                        setIsLogModalOpen(true);
                                      }}
                                    >
                                      View
                                    </Button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{entry.task}</Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{formatDurationWithUnit(entry.time)}</Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{entry.date}</Typography>
                                  </td>
                                  <td className="text-start align-middle px-4 py-2">
                                    <Typography>{formatDurationWithUnit(entry.time)}</Typography>
                                  </td>
                                  <td className="text-start align-middle action-col px-4 py-2 !whitespace-nowrap min-w-[4.75rem]">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      className="whitespace-nowrap shrink-0"
                                      onClick={() => {
                                        setSelectedLog(entry);
                                        setIsLogModalOpen(true);
                                      }}
                                    >
                                      View
                                    </Button>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              {rows.length > 0 ? (
                <ListPaginationBar
                  currentPage={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(next) => {
                    writeStoredListPageSize(
                      TIME_ENTRY_TEAM_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY,
                      next
                    );
                    setPageSize(next);
                    setPage(1);
                  }}
                  pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                  className="px-2 pb-2"
                />
              ) : null}
            </div>
          ) : null}
        </ContentLoader>
      )}

      <CommonModal
        isOpen={isDeveloperLogModalOpen && !!selectedDeveloperLog}
        onClose={() => {
          setIsDeveloperLogModalOpen(false);
          setSelectedDeveloperLog(null);
        }}
        title="Log Details"
        width="max-w-3xl"
      >
        {selectedDeveloperLog && (
          <div className="space-y-2">
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Developer:
              </Typography>{" "}
              {selectedDeveloperLog.developerName || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Team:
              </Typography>{" "}
              {selectedDeveloperLog.teamName || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Client:
              </Typography>{" "}
              {selectedDeveloperLog.clientName || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Project:
              </Typography>{" "}
              {selectedDeveloperLog.projectName || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Epic:
              </Typography>{" "}
              {selectedDeveloperLog.epicName || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Work Item:
              </Typography>{" "}
              {selectedDeveloperLog.workItemName || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Date:
              </Typography>{" "}
              {selectedDeveloperLog.date || "-"}
            </Typography>
            <Typography className="text-sm">
              <Typography variant="span2" className="font-semibold" component="span">
                Total Hours (for this date):
              </Typography>{" "}
              {selectedDeveloperLog.totalTime}
            </Typography>
            <div className="mt-3">
              <Typography className="text-sm font-semibold">Log Details</Typography>
              {Array.isArray(selectedDeveloperLog.details) &&
              selectedDeveloperLog.details.length > 0 ? (
                <ul className="list-disc pl-5 mt-1 text-sm text-text-secondary">
                  {selectedDeveloperLog.details.map((detail, idx) => (
                    <li key={detail.logId ?? idx}>
                      {detail.comment || "Log entry"}
                      {" ("}
                      {detail.loggedHours || formatDurationWithUnit(detail.minutesLogged)}
                      {")"}
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography className="text-sm text-text-secondary mt-1">
                  No log details returned by the API.
                </Typography>
              )}
            </div>
          </div>
        )}
      </CommonModal>

      <CommonModal
        isOpen={isLogModalOpen && !!selectedLog}
        onClose={() => {
          setIsLogModalOpen(false);
          setSelectedLog(null);
        }}
        title="Log Details"
        width="max-w-3xl"
      >
        {selectedLog && (
          <div className="space-y-2">
            {isTeamSummaryApiShape ? (
              <>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Team:
                  </Typography>{" "}
                  {selectedLog.teamName || "-"}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Client:
                  </Typography>{" "}
                  {selectedLog.clientName || "-"}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Project:
                  </Typography>{" "}
                  {selectedLog.projectName || "-"}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Epic:
                  </Typography>{" "}
                  {selectedLog.epicName || selectedLog.epicKey || "-"}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Total Time:
                  </Typography>{" "}
                  {formatDurationWithUnit(selectedLog.time)}
                </Typography>
                <div className="mt-3">
                  <Typography className="text-sm font-semibold">Log Details</Typography>
                  {Array.isArray(selectedLog.details) && selectedLog.details.length > 0 ? (
                    <ul className="list-disc pl-5 mt-1 text-sm text-text-secondary">
                      {selectedLog.details.map((detail, idx) => (
                        <li key={idx}>
                          <span className="font-medium">{detail.userName || "Unknown"}</span>
                          {" — "}
                          {detail.workItemTitle || detail.workItemKey
                            ? `${detail.workItemTitle || detail.workItemKey}: `
                            : ""}
                          {detail.comment || "Log entry"}
                          {" ("}
                          {detail.loggedHours || formatDurationWithUnit(detail.minutesLogged)}
                          {")"}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography className="text-sm text-text-secondary mt-1">
                      No log details returned by the API.
                    </Typography>
                  )}
                </div>
              </>
            ) : (
              <>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Team:
                  </Typography>{" "}
                  {selectedLog.team || "-"}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Team Member:
                  </Typography>{" "}
                  {selectedLog.member || "-"}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Task:
                  </Typography>{" "}
                  {selectedLog.task}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Date:
                  </Typography>{" "}
                  {selectedLog.date}
                </Typography>
                <Typography className="text-sm">
                  <Typography variant="span2" className="font-semibold" component="span">
                    Time:
                  </Typography>{" "}
                  {formatDurationWithUnit(selectedLog.time)}
                </Typography>
                <div className="mt-3">
                  <Typography className="text-sm font-semibold">Log</Typography>
                  {Array.isArray(selectedLog.logs) && selectedLog.logs.length > 0 ? (
                    <ul className="list-disc pl-5 mt-1 text-sm text-text-secondary">
                      {selectedLog.logs.map((log, idx) => (
                        <li key={idx}>{log}</li>
                      ))}
                    </ul>
                  ) : (
                    <Typography className="text-sm text-text-secondary mt-1">
                      No log available.
                    </Typography>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </CommonModal>

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
}

export default TeamTimeSummary;
